/**
 * AI Evaluation Registry — system of record for AI/model evaluations.
 */

const EVAL_TYPES = [
  'bias_fairness', 'robustness', 'cybersecurity', 'privacy', 'explainability',
  'clinical_safety', 'operational_performance', 'human_oversight',
  'vendor_provided', 'red_team',
];
const RESULT_OPTS = ['', 'pass', 'fail', 'partial', 'not_tested', 'n/a'];

function resultSelect(id) {
  return `<select id="${id}" class="form-control">${RESULT_OPTS.map(o =>
    `<option value="${o}">${o ? App.formatLabel(o) : 'Select…'}</option>`).join('')}</select>`;
}

export const aiEvaluations = {
  async aiEvaluations() {
    let evals = { data: [] };
    let assets = { data: [] };
    try { [evals, assets] = await Promise.all([API.getAiEvaluations(), API.getAssets()]); } catch (e) { /* empty */ }
    this._assets = assets.data || [];

    const rows = (evals.data || []).map(e => `
      <tr>
        <td><strong>${e.name}</strong></td>
        <td>${App.formatLabel(e.evaluation_type)}</td>
        <td>${e.asset_name || '—'}</td>
        <td>${e.pass_fail_status ? App.badge(e.pass_fail_status) : '—'}</td>
        <td>${e.risk_impact ? App.badge(e.risk_impact) : '—'}</td>
        <td>${App.badge(e.approval_status)}</td>
        <td>${App.formatDate(e.created_at)}</td>
        <td>${e.approval_status === 'pending'
          ? `<button class="btn btn-sm btn-outline" data-action="Pages.approveEvaluation" data-a0="${e.id}">Approve</button>`
          : ''}</td>
      </tr>`).join('');

    return `
      <div class="page-header">
        <div><h2>AI Evaluation Registry</h2><p>Bias, robustness, cybersecurity, privacy, clinical-safety and red-team evaluations</p></div>
        <button class="btn btn-primary" data-action="Pages.newEvaluation">+ New Evaluation</button>
      </div>
      <div class="card">
        <div class="table-container">
          <table class="data-table">
            <thead><tr>
              <th>Name</th><th>Type</th><th>AI System</th><th>Result</th>
              <th>Risk Impact</th><th>Approval</th><th>Created</th><th></th>
            </tr></thead>
            <tbody>${rows || `<tr><td colspan="8"><div class="empty-state"><div class="empty-icon">&#9673;</div><p>No evaluations yet. Create one to start building your AI evaluations evidence base.</p></div></td></tr>`}</tbody>
          </table>
        </div>
      </div>`;
  },

  newEvaluation() {
    const assetOpts = ['<option value="">— None —</option>']
      .concat((this._assets || []).map(a => `<option value="${a.id}">${a.name}</option>`)).join('');
    const typeOpts = EVAL_TYPES.map(t => `<option value="${t}">${App.formatLabel(t)}</option>`).join('');
    const body = `
      <div class="form-group"><label>Evaluation name *</label>
        <input id="ev-name" class="form-control" placeholder="e.g. Q2 Bias/Fairness Evaluation"></div>
      <div class="form-group"><label>Evaluation type *</label>
        <select id="ev-type" class="form-control">${typeOpts}</select></div>
      <div class="form-group"><label>AI system</label>
        <select id="ev-asset" class="form-control">${assetOpts}</select></div>
      <div class="form-group"><label>Model / provider</label>
        <input id="ev-provider" class="form-control" placeholder="e.g. Acme Health LLM v2"></div>
      <div class="form-group"><label>Test method</label>
        <input id="ev-method" class="form-control" placeholder="e.g. subgroup disparity analysis"></div>
      <div class="form-group"><label>Dataset used</label>
        <input id="ev-dataset" class="form-control" placeholder="e.g. 2024 validation cohort"></div>
      <div class="form-grid-2">
        <div class="form-group"><label>Bias / fairness result</label>${resultSelect('ev-r-bias')}</div>
        <div class="form-group"><label>Cybersecurity result</label>${resultSelect('ev-r-cyber')}</div>
        <div class="form-group"><label>Privacy result</label>${resultSelect('ev-r-privacy')}</div>
        <div class="form-group"><label>Robustness result</label>${resultSelect('ev-r-robust')}</div>
        <div class="form-group"><label>Explainability result</label>${resultSelect('ev-r-explain')}</div>
        <div class="form-group"><label>Human oversight result</label>${resultSelect('ev-r-oversight')}</div>
        <div class="form-group"><label>Performance result</label>${resultSelect('ev-r-perf')}</div>
      </div>
      <div class="form-grid-2">
        <div class="form-group"><label>Overall pass/fail</label>
          <select id="ev-status" class="form-control">
            <option value="pending">Pending</option><option value="pass">Pass</option>
            <option value="fail">Fail</option><option value="partial">Partial</option></select></div>
        <div class="form-group"><label>Risk impact</label>
          <select id="ev-risk" class="form-control">
            <option value="">Select…</option><option value="low">Low</option>
            <option value="moderate">Moderate</option><option value="high">High</option>
            <option value="critical">Critical</option></select></div>
      </div>
      <div class="form-group"><label>Findings</label>
        <textarea id="ev-findings" class="form-control" rows="2"></textarea></div>
      <div class="form-group"><label>Required remediation</label>
        <textarea id="ev-remediation" class="form-control" rows="2"></textarea></div>
      <div class="form-group"><label>Re-evaluation date</label>
        <input id="ev-reeval" type="date" class="form-control"></div>`;
    const footer = `
      <button class="btn btn-outline" data-action="App.closeModal">Cancel</button>
      <button class="btn btn-primary" data-action="Pages.submitEvaluation">Create Evaluation</button>`;
    App.openModal('New AI Evaluation', body, footer);
  },

  async submitEvaluation() {
    const val = (id) => (document.getElementById(id)?.value || '').trim();
    const name = val('ev-name');
    if (!name) { App.toast('Evaluation name is required', 'error'); return; }
    const body = {
      name, evaluation_type: val('ev-type'),
      ai_asset_id: val('ev-asset') || null,
      model_provider: val('ev-provider') || null,
      test_method: val('ev-method') || null,
      dataset_used: val('ev-dataset') || null,
      bias_fairness_result: val('ev-r-bias') || null,
      cybersecurity_result: val('ev-r-cyber') || null,
      privacy_result: val('ev-r-privacy') || null,
      robustness_result: val('ev-r-robust') || null,
      explainability_result: val('ev-r-explain') || null,
      human_oversight_result: val('ev-r-oversight') || null,
      performance_result: val('ev-r-perf') || null,
      pass_fail_status: val('ev-status') || 'pending',
      risk_impact: val('ev-risk') || null,
      findings: val('ev-findings') || null,
      required_remediation: val('ev-remediation') || null,
      re_evaluation_date: val('ev-reeval') || null,
    };
    try {
      await API.createAiEvaluation(body);
      App.closeModal();
      App.toast('Evaluation created', 'success');
      App.navigate('ai-evaluations');
    } catch (e) { App.toast(e.message || 'Failed to create evaluation', 'error'); }
  },

  async approveEvaluation(id) {
    try {
      await API.approveAiEvaluation(id, { approved: true });
      App.toast('Evaluation approved', 'success');
      App.navigate('ai-evaluations');
    } catch (e) { App.toast(e.message || 'Approval failed', 'error'); }
  },
};
